import graphqlResource, { GraphqlResource } from 'modules/core/graphql-resource';
import { formatDateWithTimezone } from 'shared/utils';
import { GameDetails } from '../interfaces/game-details';
import { GameDetailsDto, GameDetailsResponseDto } from '../interfaces/game-details-response-dto';
import { mapGameDetails } from '../data-mappers/game-details-mapper';
import { mapEventTypeForBackend } from '../data-mappers/game-event-type.data-mapper';

import { GamePeriodsSettings } from '../utils/match-periods-settings';
import { unwrapGraphql } from 'shared/errors';
import { ResponseGraphql } from 'shared/types/response-graphql';

export class GameRepository {
  constructor(private readonly graphqlResource: GraphqlResource) {}

  public async fetchGameDetails(gameId: number, periodsSettings: GamePeriodsSettings) {
    const query = `#graphql
      query getGameDetails($id: Int!) {
        match(id: $id) {
          id
          sportType
          date
          tournament {
            id
            name
          }
          championship {
            id
            name
          }
          teamMatches {
            team {
              id
              name
              emblem {
                externalUrl
              }
              members {
                user { id }
                position
                number
              }
              championshipApplications {
                championship { id }
                user {
                  id
                  name
                  surname
                  phone
                  birthDate
                  height
                  weight
                  photo { externalUrl }
                }
              }
            }
            score
            lineup {
              id
              name
              surname
            }
          }
          matchEvents {
            id
            second
            type
            subType
            firstTeamScore
            secondTeamScore
            videoUrl
            user {
              id
              name
              surname
            }
            playerName
            period
            secondaryUser {
              id
              name
              surname
            }
          }
          round {
            id
            name
            stage { id }
          }
          videos {
            externalUrl
          }
          participants {
            id
            match { id }
            user { id }
            isInvolved
          }
        }
      }
    `;

    const response = await this.graphqlResource.query<GameDetailsResponseDto>(query, { id: gameId });
    const { match } = unwrapGraphql(response.data);

    try {
      return mapGameDetails(match, periodsSettings);
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  public async updateGameDetails(game: GameDetails, periodSettings: GamePeriodsSettings): Promise<GameDetails> {
    const query = `#graphql
      mutation updateMatch($input: UpsertMatchInput!) {
        upsertMatch(input: $input) {
          id
          sportType
          date
          tournament {
            id
            name
          }
          championship {
            id
            name
          }
          teamMatches {
            team {
              id
              name
              emblem {
                externalUrl
              }
              members {
                user { id }
                position
                number
              }
              championshipApplications {
                championship { id }
                user {
                  id
                  name
                  surname
                  phone
                  birthDate
                  height
                  weight
                  photo { externalUrl }
                }
              }
            }
            score
            lineup {
              id
              name
              surname
            }
          }
          matchEvents {
            id
            second
            type
            firstTeamScore
            secondTeamScore
            user { id }
            playerName
            period
            secondaryUser { id }
            videoUrl
          }
          round {
            id
            name
            stage { id }
          }
          videos {
            externalUrl
          }
          participants {
            id
            match { id }
            user { id }
            isInvolved
          }
        }
      }
    `;

    const variables = {
      input: {
        tournamentId: game.tournament.id,
        championshipId: game.championship.id,
        matchId: game.id,
        firstTeamId: game.teams[0].id,
        firstTeamScore: game.result.firstTeamScore,
        secondTeamId: game.teams[1].id,
        secondTeamScore: game.result.secondTeamScore,
        sportType: game.sportType,
        date: formatDateWithTimezone(new Date(game.date)),
        matchEvents: game.events.map((event) => ({
          type: mapEventTypeForBackend(event.type),
          firstTeamScore: null,
          secondTeamScore: null,
          playerName: null,
          second: event.seconds,
          period: event.period,
          userId: event.playerIds[0],
          secondaryUserId: event.playerIds[1],
          videoUrl: event?.videoUrl,
        })),
        roundId: game.round?.id,
        videoUrls: game.videoUrls,
      },
    };

    const response = await this.graphqlResource.query<{ data: { upsertMatch: GameDetailsDto } }>(query, variables);
    return mapGameDetails(unwrapGraphql(response.data).upsertMatch, periodSettings);
  }

  public async updateMatchParticipants(matchId: number, participantIds: number[]): Promise<void> {
    const query = `
      mutation bulkChangeMatchParticipationStatus($input: BulkChangeParticipationInput!) {
        bulkChangeMatchParticipationStatus(input: $input) {
          id
          isInvolved
        }
      }
    `;

    const usersParticipation = participantIds.map((userId) => ({
      userId,
      isInvolved: true,
    }));

    const variables = {
      input: {
        matchId,
        usersParticipation,
      },
    };

    // Ответ раньше не читался вовсе — отказ проходил как успех.
    const response = await this.graphqlResource.query<ResponseGraphql<unknown>>(query, variables);
    unwrapGraphql(response.data);
  }
}

const gameRepository = new GameRepository(graphqlResource);

export default gameRepository;
